/**
 * Critically-damped spring animator for the map viewport — ramps
 * `zoom / panX / panY` simultaneously with one shared time reference
 * so the three components settle together. Parity with iOS 238.3
 * `CameraAnimator.swift` and Android `CameraAnimator.kt`.
 *
 * Settle response: `x(t) = target + (start - target) * (1 + ω·t) · e^(-ω·t)`.
 *
 * `t` is the animation's own clock. The first frame after a re-aim advances
 * it by at most {@link MAX_STEP_S}: the ~100 ms task a station tap runs
 * between `setTarget` and that frame would otherwise jump the camera a
 * quarter of the way at once (owner 2026-10-19). Later frames advance by
 * their real delta, so a slow device keeps the spring's real-time speed.
 */

export const OMEGA = 10
export const PAN_SETTLE_THRESHOLD_PX = 2
export const ZOOM_SETTLE_THRESHOLD = 0.005
/** Longest stretch of animation time the first frame after a re-aim may advance. */
export const MAX_STEP_S = 1 / 30

export interface CameraTick {
  readonly zoom: number
  readonly panX: number
  readonly panY: number
  readonly settled: boolean
}

export interface CameraAnimator {
  readonly currentZoom: number
  readonly currentPanX: number
  readonly currentPanY: number
  readonly isSettled: boolean
  /** Initialize without launching an animation — picks up externally-driven values. */
  seed(zoom: number, panX: number, panY: number): void
  /** Re-aim the spring. Current values become the new start; no discontinuity. */
  setTarget(args: { zoom: number; panX: number; panY: number; nowSec: number }): void
  /** Freeze at current values. Used when a user gesture takes control. */
  cancel(): void
  /** Integrate one frame. Idempotent when already settled. */
  tick(nowSec: number): CameraTick
}

export function createCameraAnimator(): CameraAnimator {
  let startZoom = 0
  let startPanX = 0
  let startPanY = 0
  let targetZoom = 0
  let targetPanX = 0
  let targetPanY = 0
  let animTime = 0
  let lastTick = 0
  let firstFrame = false
  let currentZoom = 0
  let currentPanX = 0
  let currentPanY = 0

  const isSettled = (): boolean =>
    Math.abs(currentZoom - targetZoom) < ZOOM_SETTLE_THRESHOLD &&
    Math.abs(currentPanX - targetPanX) < PAN_SETTLE_THRESHOLD_PX &&
    Math.abs(currentPanY - targetPanY) < PAN_SETTLE_THRESHOLD_PX

  return {
    get currentZoom() {
      return currentZoom
    },
    get currentPanX() {
      return currentPanX
    },
    get currentPanY() {
      return currentPanY
    },
    get isSettled() {
      return isSettled()
    },
    seed(zoom, panX, panY) {
      currentZoom = zoom
      currentPanX = panX
      currentPanY = panY
      startZoom = zoom
      startPanX = panX
      startPanY = panY
      targetZoom = zoom
      targetPanX = panX
      targetPanY = panY
    },
    setTarget({ zoom, panX, panY, nowSec }) {
      startZoom = currentZoom
      startPanX = currentPanX
      startPanY = currentPanY
      targetZoom = zoom
      targetPanX = panX
      targetPanY = panY
      animTime = 0
      lastTick = nowSec
      firstFrame = true
    },
    cancel() {
      targetZoom = currentZoom
      targetPanX = currentPanX
      targetPanY = currentPanY
      startZoom = currentZoom
      startPanX = currentPanX
      startPanY = currentPanY
    },
    tick(nowSec) {
      if (isSettled()) {
        currentZoom = targetZoom
        currentPanX = targetPanX
        currentPanY = targetPanY
        return { zoom: currentZoom, panX: currentPanX, panY: currentPanY, settled: true }
      }
      const delta = Math.max(0, nowSec - lastTick)
      animTime += firstFrame ? Math.min(delta, MAX_STEP_S) : delta
      firstFrame = false
      lastTick = nowSec
      const omegaT = OMEGA * animTime
      const decay = (1 + omegaT) * Math.exp(-omegaT)
      currentZoom = targetZoom + (startZoom - targetZoom) * decay
      currentPanX = targetPanX + (startPanX - targetPanX) * decay
      currentPanY = targetPanY + (startPanY - targetPanY) * decay
      return { zoom: currentZoom, panX: currentPanX, panY: currentPanY, settled: isSettled() }
    },
  }
}
